import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
        /*
         * metabus tint badges (SPEC FE-1 / R2). Status pills use a tinted bg +
         * matching border at ~12% / ~32% alpha (theme-reference 5, .cal__badge)
         * rather than a solid fill - the metabus badge recipe. Use these for
         * status pills (lead status, sequence active/paused, draft status,
         * per-network social status, call outcome) across the named surfaces.
         */
        success:
          "border-[rgba(16,185,129,0.32)] bg-[rgba(16,185,129,0.12)] text-emerald-300",
        warning:
          "border-[rgba(249,115,22,0.32)] bg-[rgba(249,115,22,0.12)] text-orange-300",
        error:
          "border-[rgba(239,68,68,0.32)] bg-[rgba(239,68,68,0.12)] text-red-300",
        info:
          "border-[rgba(124,58,237,0.32)] bg-[rgba(124,58,237,0.12)] text-violet-300",
        neutral:
          "border-border bg-muted text-muted-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  )
}

export { Badge, badgeVariants }
